/**
 * Offline catch-up + summary (Phase 2 contract).
 *
 * `runOfflineCatchup(state, nowMs, rng)` is pure: it simulates the elapsed
 * window (capped at 24h, complete intervals only), advances the state's
 * lastSimulatedAt to `nowMs` so a reload cannot re-apply the same window,
 * and returns the summary the modal renders.
 */
import { ITEM_IDS, ITEMS, SKILL_IDS } from '../../data';
import type { ItemId, PlayerState, SkillId } from '../state';
import { getMonster } from '../monsters/spec';
import { getSkill } from '../skills/spec';
import { totalBonuses } from '../economy/equipment';
import { BOSS_MONSTER_ID } from '../monsters/boss';
import {
  currentActionIntervalMs,
  OFFLINE_SIM_CAP_MS,
  simulateDetailed,
  type GameEvent,
  type LostItem,
  type Rng,
} from './simulate';

/** Show the summary modal only when at least this much time has passed. */
export const OFFLINE_MODAL_MIN_MS = 5 * 60 * 1_000;

export type OfflineSummary = {
  /** Actual wall-clock time away. */
  elapsedMs: number;
  /** Simulated time (elapsed capped at 24h). */
  simulatedMs: number;
  capped: boolean;
  /** Human label of the action that was running (e.g. "Training Token Mining"). */
  actionLabel: string;
  /** Complete action intervals processed. */
  ticks: number;
  /** Partial-interval progress banked for the next tick. */
  remainderMs: number;
  xpGained: Partial<Record<SkillId, number>>;
  levelGains: Array<{ skillId: SkillId; from: number; to: number }>;
  itemDeltas: Array<{ itemId: ItemId; delta: number }>;
  /** Gains of equippable gear — the marquee finds of the night. */
  notableDrops: ItemId[];
  /** Rolled loot that fit in neither container (bank + inventory full). */
  lostItems: ReadonlyArray<LostItem>;
  /** Transition events from the simulated window (quest completions etc.). */
  events: ReadonlyArray<GameEvent>;
  /**
   * True when an active Parakeet fight was stopped by catch-up: boss fights
   * never progress offline, so the fight resets with no damage, loot, kill,
   * or quest credit. The action label carries the player-facing explanation.
   */
  bossFightReset: boolean;
};

/** Offline settings gate: which active actions may progress while away. */
function offlineAllowed(state: PlayerState): boolean {
  switch (state.active.kind) {
    case 'combat':
      return state.settings.combatOffline;
    case 'train':
      return state.active.skillId !== 'token_mining' || state.settings.tokenMiningOffline;
    default:
      return true;
  }
}

function actionLabel(state: PlayerState): string {
  switch (state.active.kind) {
    case 'idle':
      return 'Idle';
    case 'train':
      return `Training ${getSkill(state.active.skillId).name}`;
    case 'combat':
      return `Fighting ${getMonster(state.active.monsterId).name}`;
  }
}

function itemCount(state: PlayerState, itemId: ItemId): number {
  return [...state.bank, ...state.inventory].reduce(
    (sum, s) => sum + (s?.itemId === itemId ? s.qty : 0),
    0,
  );
}

export function runOfflineCatchup(
  state: PlayerState,
  nowMs: number,
  rng: Rng,
): { state: PlayerState; summary: OfflineSummary } {
  const anchorMs = Date.parse(state.lastSimulatedAt);
  const elapsedMs = Number.isNaN(anchorMs) ? 0 : Math.max(0, nowMs - anchorMs);
  const simulatedMs = Math.min(elapsedMs, OFFLINE_SIM_CAP_MS);

  // Boss fights NEVER progress offline. A window long enough to have ticked
  // the fight stops and resets it instead — no damage, no loot, no kill, no
  // quest credit — and the summary explains why. Sub-interval windows (a
  // brief tab switch) leave the fight untouched.
  const interval = currentActionIntervalMs(state);
  const bossFightPending =
    state.active.kind === 'combat' &&
    state.active.monsterId === BOSS_MONSTER_ID &&
    interval !== null &&
    state.actionProgressMs + simulatedMs >= interval;
  if (bossFightPending) {
    const maxPlayerHp = 100 + totalBonuses(state.equipment).hp;
    const reset: PlayerState = {
      ...state,
      active: { kind: 'idle' },
      actionProgressMs: 0,
      combatRound: 0,
      hp: { player: maxPlayerHp, monster: null },
      lastSimulatedAt: new Date(nowMs).toISOString(),
    };
    return {
      state: reset,
      summary: {
        elapsedMs,
        simulatedMs,
        capped: elapsedMs > OFFLINE_SIM_CAP_MS,
        actionLabel: `${actionLabel(state)} — boss fights never progress offline; the fight was stopped and reset (no damage, no loot)`,
        ticks: 0,
        remainderMs: 0,
        xpGained: {},
        levelGains: [],
        itemDeltas: [],
        notableDrops: [],
        lostItems: [],
        events: [],
        bossFightReset: true,
      },
    };
  }

  // Settings can veto offline progress for the running action. The anchor
  // still advances below, so the disabled window can never be claimed later.
  const allowed = offlineAllowed(state);
  const { state: after, ticks, lostItems, events } = allowed
    ? simulateDetailed(state, simulatedMs, { capMs: OFFLINE_SIM_CAP_MS, rng })
    : {
        state,
        ticks: 0,
        lostItems: [] as ReadonlyArray<LostItem>,
        events: [] as ReadonlyArray<GameEvent>,
      };

  const itemDeltas: OfflineSummary['itemDeltas'] = [];
  for (const itemId of ITEM_IDS) {
    const delta = itemCount(after, itemId) - itemCount(state, itemId);
    if (delta !== 0) itemDeltas.push({ itemId, delta });
  }

  const xpGained: OfflineSummary['xpGained'] = {};
  const levelGains: OfflineSummary['levelGains'] = [];
  for (const skillId of SKILL_IDS) {
    const before = state.skills[skillId];
    const afterSkill = after.skills[skillId];
    if (afterSkill.xp > before.xp) xpGained[skillId] = afterSkill.xp - before.xp;
    if (afterSkill.level > before.level) {
      levelGains.push({ skillId, from: before.level, to: afterSkill.level });
    }
  }

  return {
    // Advance the simulation timestamp immediately: the caller persists this
    // state right away, so a reload cannot double-apply the same window.
    state: { ...after, lastSimulatedAt: new Date(nowMs).toISOString() },
    summary: {
      elapsedMs,
      simulatedMs,
      capped: elapsedMs > OFFLINE_SIM_CAP_MS,
      actionLabel: allowed
        ? actionLabel(state)
        : `${actionLabel(state)} (offline progress disabled in settings)`,
      ticks,
      remainderMs: after.actionProgressMs,
      xpGained,
      levelGains,
      itemDeltas,
      notableDrops: itemDeltas
        .filter((d) => d.delta > 0 && ITEMS.items[d.itemId]?.equip)
        .map((d) => d.itemId),
      lostItems,
      events,
      bossFightReset: false,
    },
  };
}
